import { DateTime } from 'luxon'
import { TGoal, TGoalReviewEntry, TGoalReviewFix, TGoalReviewRead, formatGoalAmount, isGoalClosed } from './goalTypes'

/*
 * The weekly review's rules, kept apart from the components that draw them (GoalReviewPane,
 * GoalReviewNudge, GoalDetails, GoalsList) so the four surfaces cannot disagree about which goals
 * are walked, when a review is finished, or what a saved answer reads as.
 *
 * Two things here are deliberately not derived on the client: the review week itself (the server
 * keys it from the request's Timezone-Offset and returns `weekStart`), and the goal's status badge.
 * The read recorded here is the owner's opinion and is always shown beside that badge, never as it.
 */

/** The current review week, as GET /goals/reviews/current/ returns it. */
export interface TGoalReview {
    /** The Sunday this review week starts on, yyyy-MM-dd. */
    weekStart: string
    /** The Sunday the next review opens, yyyy-MM-dd. */
    nextReview: string
    /** "Not this week" on the Daily Overview card. */
    dismissed: boolean
    entries: TGoalReviewEntry[]
}

export const GOAL_REVIEW_READS: { value: TGoalReviewRead; label: string; description: string }[] = [
    { value: 'moving', label: 'Moving', description: 'It’s going the way I meant.' },
    { value: 'stuck', label: 'Stuck', description: 'I want it, but it isn’t happening.' },
    { value: 'not_now', label: 'Not important right now', description: 'Something else matters more.' },
    { value: 'done', label: 'Done', description: 'I got what I came for.' },
]

export const GOAL_REVIEW_READ_LABEL = Object.fromEntries(
    GOAL_REVIEW_READS.map((read) => [read.value, read.label])
) as Record<TGoalReviewRead, string>

export const GOAL_REVIEW_FIX_LABEL: Record<TGoalReviewFix, string> = {
    ease: 'Eased back',
    extend: 'Runway extended',
    keep: 'Plan kept',
    rethink: 'Redraft opened',
    pause: 'Paused',
    archive: 'Archived',
    archive_done: 'Archived as done',
    achieve: 'Marked achieved',
    new_target: 'New target',
}

/** "Sun Oct 11" — the one date format the review uses everywhere. */
export const reviewDateLabel = (isoDate: string): string => DateTime.fromISO(isoDate).toFormat('ccc LLL d')

const weekdayLabel = (isoDate: string): string => DateTime.fromISO(isoDate).toFormat('ccc')

/** A read needs no change only when it is "moving"; a step belongs to a goal that stays in play. */
export const readTakesStep = (read?: TGoalReviewRead): boolean => read === 'moving' || read === 'stuck'

/**
 * A goal the review walks. Paused goals are not walked — there is no pace to read — and archived
 * goals have left the list; both rules match what the server accepts an answer for.
 */
export const isReviewable = (goal: TGoal): boolean => !isGoalClosed(goal) && goal.status !== 'paused'

export const entryForGoal = (review: TGoalReview | undefined, goalId: string): TGoalReviewEntry | undefined =>
    review?.entries.find((entry) => entry.goalId === goalId)

/**
 * The review's queue, in list order: every goal still walked, plus any goal already answered this
 * week. The second half matters because answering can take a goal out of the first — pausing or
 * archiving it — and it must keep its place in the count and the summary rather than vanish.
 */
export const reviewQueue = (goals: TGoal[], review: TGoalReview | undefined): TGoal[] =>
    goals.filter((goal) => isReviewable(goal) || !!entryForGoal(review, goal.id))

const unreadGoals = (goals: TGoal[], review: TGoalReview | undefined): TGoal[] =>
    goals.filter((goal) => isReviewable(goal) && !entryForGoal(review, goal.id))

/** The next goal without an answer, looking forward from `afterGoalId` and wrapping; null when none. */
export const nextUnreadGoalId = (
    goals: TGoal[],
    review: TGoalReview | undefined,
    afterGoalId?: string | null
): string | null => {
    const unread = unreadGoals(goals, review).filter((goal) => goal.id !== afterGoalId)
    if (unread.length === 0) return null
    const from = goals.findIndex((goal) => goal.id === afterGoalId)
    const later = unread.find((goal) => goals.indexOf(goal) > from)
    return (later ?? unread[0]).id
}

/** Finished means nothing walkable is left unanswered — and that there was something to review. */
export const isReviewComplete = (goals: TGoal[], review: TGoalReview | undefined): boolean =>
    reviewQueue(goals, review).length > 0 && unreadGoals(goals, review).length === 0

const stepLabel = (entry: TGoalReviewEntry, separator: string): string =>
    entry.stepTitle ? `${entry.stepTitle}${entry.stepDay ? `${separator}${weekdayLabel(entry.stepDay)}` : ''}` : ''

/** One answer as a line: "Stuck · Eased back · Chapter 4 of Piranesi, Thu". Used by cards and the summary. */
export const reviewEntrySummary = (entry: TGoalReviewEntry): string => {
    if (entry.skipped || !entry.read) return 'Skipped'
    const step = stepLabel(entry, ', ') || (readTakesStep(entry.read) ? 'No step' : '')
    return [GOAL_REVIEW_READ_LABEL[entry.read], entry.fix && GOAL_REVIEW_FIX_LABEL[entry.fix], step]
        .filter(Boolean)
        .join(' · ')
}

/**
 * What the change was, as a sentence about the goal as it stands. Only "ease" and "extend" carry a
 * number, and it is read from the goal rather than stored with the answer: the goal is the truth
 * about its own target, and a copy here would go stale the first time it was edited.
 */
const fixSentence = (goal: TGoal, fix?: TGoalReviewFix): string => {
    if (!fix) return ''
    if (fix === 'ease' && goal.weeklyTarget) {
        return `Eased back to ${formatGoalAmount(goal.weeklyTarget)}${goal.unit === 'hrs' ? ' hrs' : '×'} a week.`
    }
    if (fix === 'extend' && goal.endDate) {
        return `Runway extended to ${DateTime.fromISO(goal.endDate).toFormat('LLL d')}.`
    }
    return `${GOAL_REVIEW_FIX_LABEL[fix]}.`
}

/** How long a read keeps its place before it drops to muted text. */
export const REVIEW_READ_STALE_DAYS = 14

export interface TGoalReadStrip {
    /** "Sun Oct 11" */
    date: string
    read: string
    /** "Eased back to 1× a week." — empty when the read needed no change. */
    detail: string
    isStale: boolean
}

/**
 * The strip under a goal's chips: the owner's last read, beside the server badge. Hidden for a goal
 * that was never reviewed, and for a paused or archived one, whose banner already says where it stands.
 */
export const goalReadStrip = (goal: TGoal, now: DateTime): TGoalReadStrip | null => {
    if (!isReviewable(goal)) return null
    const latest = goal.reviews?.find((entry) => !entry.skipped && entry.read)
    if (!latest?.read) return null
    const at = DateTime.fromISO(latest.at)
    return {
        date: at.toFormat('ccc LLL d'),
        read: GOAL_REVIEW_READ_LABEL[latest.read],
        detail: fixSentence(goal, latest.fix),
        isStale: now.startOf('day').diff(at.startOf('day'), 'days').days > REVIEW_READ_STALE_DAYS,
    }
}

/** A review as a row of Recent: "Weekly review · Stuck · eased back to 1× a week". */
export const reviewRecentTitle = (goal: TGoal, entry: TGoalReviewEntry): string => {
    if (entry.skipped || !entry.read) return 'Weekly review · Skipped'
    const detail = fixSentence(goal, entry.fix).replace(/\.$/, '')
    return ['Weekly review', GOAL_REVIEW_READ_LABEL[entry.read], detail && detail[0].toLowerCase() + detail.slice(1)]
        .filter(Boolean)
        .join(' · ')
}

/**
 * The task tagged "This week’s step" in Next up: the step picked in the current review week. Once
 * the next review week opens, last week's step is just a task again.
 */
export const weekStepTaskId = (goal: TGoal, review: TGoalReview | undefined): string | undefined => {
    const latest = goal.reviews?.[0]
    return latest && review && latest.weekStart === review.weekStart ? latest.stepTaskId : undefined
}

/**
 * "Last week you picked Easy 5k. Done." — the step from the most recent earlier review, so this
 * week's read starts from what was actually promised. `isDone` is undefined when the task is gone.
 */
export const lastStepLine = (goal: TGoal, review: TGoalReview, isDone: (taskId: string) => boolean | undefined) => {
    const previous = goal.reviews?.find((entry) => entry.weekStart !== review.weekStart && entry.stepTitle)
    if (!previous?.stepTaskId || !previous.weekStart) return null
    const weeksAgo = Math.round(
        DateTime.fromISO(review.weekStart).diff(DateTime.fromISO(previous.weekStart), 'weeks').weeks
    )
    const done = isDone(previous.stepTaskId)
    return [
        `${weeksAgo === 1 ? 'Last week' : 'Last time'} you picked ${previous.stepTitle}.`,
        done === undefined ? '' : done ? 'Done.' : 'Still open.',
    ]
        .filter(Boolean)
        .join(' ')
}

/** The Sunday the evidence card looks back to: the start of the week this review closes. */
export const reviewCoversSince = (review: TGoalReview): string =>
    reviewDateLabel(DateTime.fromISO(review.weekStart).minus({ weeks: 1 }).toISODate())

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`

export type TReviewNudge =
    | { kind: 'due' | 'open' | 'continue'; title: string; body: string; action: string }
    | { kind: 'done'; title: string; body: string }

interface ReviewNudgeState {
    goals: TGoal[]
    /** Undefined while loading or after a failed request: the week is unknown, so nothing is said. */
    review: TGoalReview | undefined
    areGoalsLoading: boolean
    now: DateTime
}

/**
 * The single owner of what the Daily Overview card says, or null for "say nothing".
 *
 * Silent unless both the goals and the week are actually known, for the reason noGoalReason is: a
 * card that blinks in and is then withdrawn reads as the app being broken. It needs at least one
 * goal to walk, which is what makes it mutually exclusive with NoGoalNudge — that one only speaks
 * when there are none.
 */
export const reviewNudge = ({ goals, review, areGoalsLoading, now }: ReviewNudgeState): TReviewNudge | null => {
    if (areGoalsLoading || !review || review.dismissed) return null
    const queue = reviewQueue(goals, review)
    // No live goal left to walk — including when this review paused or archived the last one — is
    // NoGoalNudge's case. The slot holds one card, so this one stands down rather than sit beside it.
    if (queue.length === 0 || !goals.some(isReviewable)) return null

    if (isReviewComplete(goals, review)) {
        // The finished card is a receipt for the day the review was done, not a week-long fixture.
        const lastAnswer = review.entries.reduce((latest, entry) => (entry.at > latest ? entry.at : latest), '')
        if (!lastAnswer || !DateTime.fromISO(lastAnswer).hasSame(now, 'day')) return null
        const steps = review.entries.map((entry) => stepLabel(entry, ' ')).filter(Boolean)
        return {
            kind: 'done',
            title: 'Reviewed today',
            body: [
                steps.length > 0 && `This week: ${steps.join(' · ')}.`,
                `Next review ${reviewDateLabel(review.nextReview)}.`,
            ]
                .filter(Boolean)
                .join(' '),
        }
    }

    const answered = queue.filter((goal) => entryForGoal(review, goal.id)).length
    if (answered > 0) {
        return {
            kind: 'continue',
            title: 'Weekly review',
            body: `${answered} of ${plural(queue.length, 'goal')} read · your answers are saved`,
            action: 'Continue review',
        }
    }

    const scope = `${plural(queue.length, 'goal')} · about ${plural(queue.length, 'minute')}`
    const since = `covers since ${reviewCoversSince(review)}`
    // Sunday is the review day; from Monday the same review is simply still open.
    const isReviewDay = now.toISODate() === review.weekStart
    return isReviewDay
        ? { kind: 'due', title: 'Weekly review', body: `${scope} · ${since}`, action: 'Start review' }
        : {
              kind: 'open',
              title: 'Weekly review',
              body: `Open until Saturday · ${plural(queue.length, 'goal')} · ${since}`,
              action: 'Start review',
          }
}

/**
 * The Goals action row's entry: where the review stands, in the fewest words that are still true.
 * Null when there is nothing to review, so an account with no live goals sees no button.
 */
export const reviewEntryLabel = (goals: TGoal[], review: TGoalReview | undefined): string | null => {
    if (!review || reviewQueue(goals, review).length === 0) return null
    if (isReviewComplete(goals, review)) return `Weekly review · next ${reviewDateLabel(review.nextReview)}`
    return review.entries.length > 0 ? 'Continue review' : 'Weekly review'
}

/** The date a weekday chip stands for: the next such day from `now`, today included. */
export const REVIEW_STEP_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

export const stepDayDate = (weekday: number, now: DateTime): string =>
    now
        .startOf('day')
        .plus({ days: (weekday - now.weekday + 7) % 7 })
        .toISODate()
